/**
 * Socket.IO event names. The only source of truth for both sides of the wire.
 * Server emits everything in SERVER_EVENTS; clients emit CLIENT_EVENTS.
 */

export const SERVER_EVENTS = {
  MESSAGE_NEW: 'message:new',
  /**
   * An existing message was edited or withdrawn by an agent.
   *
   * Its own event rather than a re-emitted MESSAGE_NEW: both sides already
   * have the row and must patch it in place. A re-send would append a second
   * copy on the visitor's side and re-chime, which is the opposite of what
   * withdrawing a message is for.
   */
  MESSAGE_UPDATED: 'message:updated',
  MESSAGE_READ: 'message:read',
  TYPING: 'typing',
  CONVERSATION_UPDATED: 'conversation:updated',
  TICKET_UPDATED: 'ticket:updated',
  /**
   * A conversation or a ticket was handed to an agent.
   *
   * Emitted into the app room rather than to one socket: the room is what the
   * server can address, and every dashboard already filters what it renders by
   * the signed-in user. The payload names the assignee so the client can tell
   * "this is yours" from "someone else took it".
   */
  ASSIGNMENT_NEW: 'assignment:new',
  /**
   * Visitor-initiated connection test. Carries no message and writes nothing —
   * it exists so the widget can prove the path to an agent's dashboard end to
   * end without leaving a stray message in the thread.
   */
  DIAGNOSTIC_PING: 'diagnostic:ping',
  /**
   * A visitor arrived or left. Emitted into the app room on the *transition*
   * only, after a short grace period, so a reconnect blip does not flap. This
   * is what keeps an agent's online list live without polling.
   */
  PRESENCE_CHANGED: 'presence:changed',
  /**
   * The visitor opened the chat panel.
   *
   * Distinct from PRESENCE_CHANGED, which fires when the widget's socket
   * connects — that is "the page with the widget on it loaded", which happens
   * on every navigation and says nothing about intent. This one fires when
   * somebody actually reaches for support, so it is the stronger of the two
   * signals and the one an alert mode should be able to ring on separately.
   *
   * Carries no message and writes nothing.
   */
  CHAT_OPENED: 'chat:opened',
  /**
   * Which agents currently have a conversation open, oldest arrival first.
   *
   * Derived from socket room membership rather than announced by the client,
   * so it cannot claim a seat it does not hold and cannot leave one behind
   * when a laptop lid closes. Emitted into the app room, not the conversation
   * room: an agent about to open a thread needs to know somebody is already in
   * it *before* they join, which is exactly when they are not in the room yet.
   *
   * The order is the whole point — the first agent in owns the reply, and
   * everyone after them is asked to confirm before they send.
   */
  CONVERSATION_VIEWERS: 'conversation:viewers',
  ERROR: 'realtime:error',
};

export const CLIENT_EVENTS = {
  CONVERSATION_SUBSCRIBE: 'conversation:subscribe',
  CONVERSATION_UNSUBSCRIBE: 'conversation:unsubscribe',
  TYPING: 'typing',
  MESSAGE_READ: 'message:read',
  /** Visitor reached for support. Relayed to agents as SERVER_EVENTS.CHAT_OPENED. */
  CHAT_OPENED: 'chat:opened',
};

/** Room name helpers — used by the server, exported so clients can reason about scope. */
export const rooms = {
  app: (appId) => `app:${appId}`,
  conversation: (conversationId) => `conv:${conversationId}`,
};

export default { SERVER_EVENTS, CLIENT_EVENTS, rooms };
